import React, { forwardRef } from 'react';

import type { TextareaAutosizeProps } from '@mui/material';
import { FormHelperText, TextareaAutosize } from '@mui/material';
import type { StandardLonghandProperties } from 'csstype';
import { useField } from 'formik';
import { getAlinePlaceholderCSSProps } from 'src/AlineInput';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

export const useCommonTextareaStyles = makeStyles<StyleProps>()((theme, { disabled, error }) => ({
  textArea: {
    ...getTypographyForSx(theme.typography.body1),
    '&::placeholder': {
      ...getAlinePlaceholderCSSProps(theme),
    },
    '&:focus': {
      border: `1px solid ${error ? theme.palette.error.main : theme.appColors.primary}`,
    },
    border: `solid 1px ${error ? theme.palette.error.main : theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    color: disabled ? theme.palette.text.disabled : theme.appColors.iconsType,
    outline: 'none',
    padding: theme.spacing(1),
    resize: 'none',
    width: '100%',
  },
}));

interface StyleProps {
  disabled?: boolean;
  error?: boolean;
}

interface TextAreaProps extends TextareaAutosizeProps {
  fieldName: string;
  alignHelperText?: StandardLonghandProperties['textAlign'];
  showHelperText?: boolean;
}

const FormikAlineTextareaWithRef = forwardRef(function FormikAlineTextarea(
  props: TextAreaProps,
  ref: React.Ref<HTMLTextAreaElement>
): JSX.Element {
  const { alignHelperText = 'left', className, fieldName, showHelperText = true, ...rest } = props;
  const [field, meta] = useField<string>(fieldName);
  const errorMsg = meta.touched && typeof meta.error === 'string' ? meta.error : null;
  const { classes } = useCommonTextareaStyles({
    disabled: rest.disabled,
    error: Boolean(errorMsg),
  });

  // The ' ' ensures that the input doesn't jump when an error renders b/c '' renders at 0 height.
  return (
    <div className={className}>
      <TextareaAutosize className={classes.textArea} {...rest} {...field} ref={ref} />
      {showHelperText && (
        <FormHelperText error style={{ textAlign: alignHelperText }}>
          {errorMsg || ' '}
        </FormHelperText>
      )}
    </div>
  );
});

export const FormikAlineTextarea = React.memo(FormikAlineTextareaWithRef);
